import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../supabaseClient';
import { notifyTrial } from '../../database/databaseNotifications';

export default function AdminTrialRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRequests = useCallback(async () => {
    setLoading(true);

    // Fetch pending trial bonuses with client info
    const { data: bonuses, error } = await supabase
      .from('bonus')
      .select(`
        id,
        created_at,
        from_date,
        price,
        user_id,
        bonustype (name),
        profiles (full_name, email)
      `)
      .eq('status', 'pending_trial')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading trial requests:', error.message);
      setLoading(false);
      return;
    }

    // For each bonus, find the associated class reservation
    const enriched = await Promise.all(
      (bonuses || []).map(async (b) => {
        const { data: cp } = await supabase
          .from('classprofile')
          .select(`
            id,
            calendar_class (
              date,
              starttime,
              endtime,
              class (title)
            )
          `)
          .eq('bonus', b.id)
          .maybeSingle();

        return { ...b, classprofile: cp };
      })
    );

    setRequests(enriched);
    setLoading(false);
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const handleApprove = async (bonusId, request) => {
    const { error } = await supabase
      .from('bonus')
      .update({ status: null })
      .eq('id', bonusId);

    if (error) {
      alert(`Error al aprobar: ${error.message}`);
      return;
    }

    const className = request.classprofile?.calendar_class?.class?.title;
    const dateTime = formatDateTime(request.classprofile?.calendar_class?.date, request.classprofile?.calendar_class?.starttime);
    notifyTrial('approved', request.profiles?.email, className, dateTime);

    await loadRequests();
  };

  const handleReject = async (bonusId, classprofileId, request) => {
    if (!window.confirm('¿Rechazar esta solicitud? Se cancelará la reserva del cliente.')) return;

    if (classprofileId) {
      const { error: cpError } = await supabase
        .from('classprofile')
        .delete()
        .eq('id', classprofileId);
      if (cpError) {
        alert(`Error al cancelar la reserva: ${cpError.message}`);
        return;
      }
    }

    const { error } = await supabase.from('bonus').delete().eq('id', bonusId);
    if (error) {
      alert(`Error al rechazar: ${error.message}`);
      return;
    }

    const className = request.classprofile?.calendar_class?.class?.title;
    const dateTime = formatDateTime(request.classprofile?.calendar_class?.date, request.classprofile?.calendar_class?.starttime);
    notifyTrial('rejected', request.profiles?.email, className, dateTime);

    await loadRequests();
  };

  const formatDateTime = (date, starttime) => {
    if (!date) return '—';
    const d = new Date(`${date}T00:00:00`).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    const t = starttime ? String(starttime).slice(0, 5) : '';
    return t ? `${d} ${t}` : d;
  };

  const formatCreatedAt = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <section className="w-[95%] md:w-[90%] lg:w-[80%] mx-auto py-6">
      {loading ? (
        <p className="text-ink/60">Cargando…</p>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto rounded border border-sand">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="bg-sand text-left text-terracotta-dark">
                  <th className="border px-3 py-2">Cliente</th>
                  <th className="border px-3 py-2">Email</th>
                  <th className="border px-3 py-2">Clase</th>
                  <th className="border px-3 py-2">Fecha / Hora</th>
                  <th className="border px-3 py-2">Solicitado</th>
                  <th className="border px-3 py-2 text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="hover:bg-sand/40">
                    <td className="border px-3 py-2">{r.profiles?.full_name ?? '—'}</td>
                    <td className="border px-3 py-2">{r.profiles?.email ?? '—'}</td>
                    <td className="border px-3 py-2">{r.classprofile?.calendar_class?.class?.title ?? '—'}</td>
                    <td className="border px-3 py-2 whitespace-nowrap">
                      {formatDateTime(r.classprofile?.calendar_class?.date, r.classprofile?.calendar_class?.starttime)}
                    </td>
                    <td className="border px-3 py-2 whitespace-nowrap">{formatCreatedAt(r.created_at)}</td>
                    <td className="border px-3 py-2 text-center">
                      <div className="flex items-center justify-center gap-3">
                        <button
                          onClick={() => handleApprove(r.id, r)}
                          className="text-terracotta hover:underline font-medium"
                        >
                          Aprobar
                        </button>
                        <button
                          onClick={() => handleReject(r.id, r.classprofile?.id, r)}
                          className="text-red-600 hover:underline"
                        >
                          Rechazar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-ink/60">
                      No hay solicitudes pendientes.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {requests.length === 0 && (
              <div className="text-center py-4 border border-sand rounded text-ink/60">No hay solicitudes pendientes.</div>
            )}
            {requests.map((r) => (
              <div key={r.id} className="border border-sand rounded-lg p-4 shadow-sm bg-card">
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex gap-2">
                    <span className="w-[40%] font-medium text-ink/70">Cliente</span>
                    <span className="w-[60%]">{r.profiles?.full_name ?? '—'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-[40%] font-medium text-ink/70">Email</span>
                    <span className="w-[60%] break-all">{r.profiles?.email ?? '—'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-[40%] font-medium text-ink/70">Clase</span>
                    <span className="w-[60%]">{r.classprofile?.calendar_class?.class?.title ?? '—'}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-[40%] font-medium text-ink/70">Fecha</span>
                    <span className="w-[60%]">
                      {formatDateTime(r.classprofile?.calendar_class?.date, r.classprofile?.calendar_class?.starttime)}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <span className="w-[40%] font-medium text-ink/70">Solicitado</span>
                    <span className="w-[60%]">{formatCreatedAt(r.created_at)}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-between gap-2">
                  <button
                    onClick={() => handleApprove(r.id, r)}
                    className="flex-1 py-2 rounded border text-terracotta border-terracotta/30 font-medium"
                  >
                    Aprobar
                  </button>
                  <button
                    onClick={() => handleReject(r.id, r.classprofile?.id, r)}
                    className="flex-1 py-2 rounded border text-red-700 border-red-200"
                  >
                    Rechazar
                  </button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
